import { useState, useRef, useEffect } from 'react'
import { useNavigate } from 'react-router-dom'
import { useMutation } from '@tanstack/react-query'
import { TrendingUp, Users, Percent, Star, CheckCircle2, Loader2, Mail, Palette, Banknote, Globe, Shield, Building2, Zap, ArrowRight } from 'lucide-react'
import ReCAPTCHA from 'react-google-recaptcha'
import { Card } from '@/components/ui/Card'

interface ResellerApplication {
  first_name: string
  last_name: string
  email: string
  phone: string
  company_name: string
  website: string
  expected_monthly_sales: number
  experience: string
}

const BENEFITS = [
  { icon: Percent, title: 'Competitive Margins', description: 'Earn up to 30% commission on all sales' },
  { icon: Users, title: 'Dedicated Support', description: 'Reseller support Mon–Fri 9am–5pm, plus 24-hour chat' },
  { icon: TrendingUp, title: 'Marketing Materials', description: 'Free marketing and sales materials' },
  { icon: Star, title: 'Priority Service', description: 'Fast-track order processing' },
]

const TIERS = [
  {
    name: 'Bronze',
    sales: '0-50',
    commission: '15%',
    color: 'from-amber-600 to-orange-600',
    features: ['Basic support', 'Marketing materials', 'Monthly payouts'],
  },
  {
    name: 'Silver',
    sales: '51-100',
    commission: '20%',
    color: 'from-gray-400 to-gray-600',
    features: ['Priority support', 'Custom materials', 'Bi-weekly payouts', 'Dedicated account manager'],
  },
  {
    name: 'Gold',
    sales: '101+',
    commission: '30%',
    color: 'from-yellow-500 to-yellow-600',
    features: ['VIP support', 'Co-branded materials', 'Weekly payouts', 'Personal account manager', 'Special rates'],
  },
]

export default function ResellerProgram() {
  const navigate = useNavigate()

  useEffect(() => {
    window.scrollTo({ top: 0, behavior: 'instant' as ScrollBehavior })
    // Track page visit; pass ref code if present in URL
    const ref = new URLSearchParams(window.location.search).get('ref')
    const url = ref ? `/api/reseller/track/click?ref=${encodeURIComponent(ref)}` : '/api/reseller/track/click'
    fetch(url, { method: 'POST' }).catch(() => {})
  }, [])

  const [formData, setFormData] = useState<ResellerApplication>({
    first_name: '',
    last_name: '',
    email: '',
    phone: '',
    company_name: '',
    website: '',
    expected_monthly_sales: 0,
    experience: '',
  })
  const [showSuccess, setShowSuccess] = useState(false)
  const [errorMessage, setErrorMessage] = useState<string | null>(null)
  const [recaptchaToken, setRecaptchaToken] = useState<string | null>(null)
  const recaptchaRef = useRef<ReCAPTCHA>(null)

  const applyMutation = useMutation({
    mutationFn: async (data: ResellerApplication & { recaptcha_token: string | null }) => {
      const response = await fetch('/api/reseller/apply', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(data),
      })
      if (!response.ok) {
        const err = await response.json()
        throw new Error(err.detail || 'Application failed')
      }
      return response.json()
    },
    onSuccess: () => {
      setShowSuccess(true)
      setErrorMessage(null)
      setFormData({
        first_name: '',
        last_name: '',
        email: '',
        phone: '',
        company_name: '',
        website: '',
        expected_monthly_sales: 0,
        experience: '',
      })
      setRecaptchaToken(null)
      recaptchaRef.current?.reset()
    },
    onError: (err: Error) => {
      setErrorMessage(err.message || 'Something went wrong. Please try again.')
      recaptchaRef.current?.reset()
      setRecaptchaToken(null)
    },
  })

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault()
    // Accept bare domains (silux.co.uk / www.silux.co.uk) — prepend https:// if no scheme given.
    const site = formData.website.trim()
    const normalizedWebsite = site && !/^https?:\/\//i.test(site) ? `https://${site}` : site
    applyMutation.mutate({ ...formData, website: normalizedWebsite, recaptcha_token: recaptchaToken })
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 via-primary-50 to-primary-50">
      {/* Hero */}
      <div className="relative overflow-hidden bg-gradient-to-br from-primary-600 via-primary-500 to-pink-600 text-white">
        <div className="absolute inset-0 bg-grid-white/10"></div>
        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-20">
          <div className="text-center">
            <div className="inline-flex p-4 bg-white/20 backdrop-blur-sm rounded-2xl mb-6">
              <TrendingUp className="w-16 h-16" />
            </div>
            <h1 className="text-5xl md:text-6xl font-bold mb-6">
              Become a Reseller
            </h1>
            <p className="text-2xl text-primary-100 mb-8 max-w-3xl mx-auto">
              Partner with Silux Mobile and earn competitive commissions
            </p>
            <div className="flex flex-wrap items-center justify-center gap-8 text-lg">
              <div className="flex items-center gap-2">
                <CheckCircle2 className="w-6 h-6" />
                <span>Your Brand, Your Portal</span>
              </div>
              <div className="flex items-center gap-2">
                <CheckCircle2 className="w-6 h-6" />
                <span>Set Your Own Prices &amp; Margins</span>
              </div>
              <div className="flex items-center gap-2">
                <CheckCircle2 className="w-6 h-6" />
                <span>No Targets, No Restrictions</span>
              </div>
            </div>
          </div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16">
        {/* Success Message */}
        {showSuccess && (
          <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50 backdrop-blur-sm" onClick={() => { window.scrollTo({ top: 0, behavior: 'instant' as ScrollBehavior }); navigate('/') }}>
            <div
              className="bg-white rounded-2xl shadow-2xl max-w-md w-full p-8 text-center animate-scale-in"
              onClick={(e) => e.stopPropagation()}
            >
              <div className="flex justify-center mb-5">
                <div className="w-20 h-20 rounded-full bg-green-100 flex items-center justify-center">
                  <CheckCircle2 className="w-12 h-12 text-green-500" />
                </div>
              </div>
              <h3 className="text-2xl font-bold text-gray-900 mb-3">Application Submitted Successfully</h3>
              <p className="text-gray-600 mb-2">Thank you, we have received your reseller application.</p>
              <p className="text-gray-500 text-sm mb-8">Our team will review it and get back to you within 2–3 business days. A confirmation has been sent to your email and phone.</p>
              <button
                onClick={() => { window.scrollTo({ top: 0, behavior: 'instant' as ScrollBehavior }); navigate('/') }}
                className="w-full py-3 px-6 bg-gradient-to-r from-primary-600 to-primary-500 hover:from-primary-700 hover:to-primary-600 text-white font-bold rounded-xl transition-all"
              >
                Close
              </button>
            </div>
          </div>
        )}

        {/* ── Programme Introduction ── */}
        <div className="text-center max-w-3xl mx-auto mb-16">
          <span className="inline-block bg-primary-100 text-primary-700 text-xs font-black uppercase tracking-widest px-4 py-1.5 rounded-full mb-4">
            Reseller Programme
          </span>
          <h2 className="text-3xl sm:text-4xl font-bold text-gray-900 mb-5 leading-tight">
            Build a telecoms business<br className="hidden sm:block" /> under your own brand
          </h2>
          <p className="text-gray-600 text-lg leading-relaxed">
            The Silux Mobile Reseller Programme gives you everything you need to run a fully branded
            telecoms business — without the infrastructure costs. Sell SIM plans, broadband, and bundles
            through your own personalised portal, at your own prices, keeping the profits your way.
          </p>
        </div>

        {/* ── White Label + Pricing Freedom ── */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-8 mb-16">
          {/* White Label */}
          <div className="relative overflow-hidden rounded-3xl bg-gradient-to-br from-primary-600 to-primary-800 text-white p-8 sm:p-10 shadow-xl">
            <div className="absolute -top-10 -right-10 w-48 h-48 bg-white/5 rounded-full" />
            <div className="absolute -bottom-8 -left-8 w-36 h-36 bg-white/5 rounded-full" />
            <div className="relative">
              <div className="inline-flex p-3 bg-white/15 rounded-2xl mb-5">
                <Palette className="w-8 h-8 text-white" />
              </div>
              <h3 className="text-2xl font-black mb-3">White Label Branding</h3>
              <p className="text-primary-100 mb-6 leading-relaxed">
                Your brand. Your logo. Your portal. Customers never need to know who powers you —
                everything they see is 100% yours. Build trust and loyalty under a brand you own.
              </p>
              <ul className="space-y-3">
                {[
                  'Your own logo and colour scheme throughout',
                  'Custom domain — sell from your own website',
                  'Branded customer portal and account pages',
                  'Personalised emails and notifications',
                  'Your business name on all customer comms',
                ].map((item, i) => (
                  <li key={i} className="flex items-start gap-3 text-sm text-primary-100">
                    <CheckCircle2 className="w-4 h-4 text-green-300 mt-0.5 flex-shrink-0" />
                    {item}
                  </li>
                ))}
              </ul>
            </div>
          </div>

          {/* Pricing Freedom */}
          <div className="relative overflow-hidden rounded-3xl bg-gradient-to-br from-emerald-600 to-teal-700 text-white p-8 sm:p-10 shadow-xl">
            <div className="absolute -top-10 -right-10 w-48 h-48 bg-white/5 rounded-full" />
            <div className="absolute -bottom-8 -left-8 w-36 h-36 bg-white/5 rounded-full" />
            <div className="relative">
              <div className="inline-flex p-3 bg-white/15 rounded-2xl mb-5">
                <Banknote className="w-8 h-8 text-white" />
              </div>
              <h3 className="text-2xl font-black mb-3">Your Pricing, Your Profits</h3>
              <p className="text-emerald-100 mb-6 leading-relaxed">
                Set your own sales prices and keep the margin. Buy at our competitive wholesale rates
                and sell at whatever price works for your market. No restrictions, no interference —
                your profit structure is entirely in your hands.
              </p>
              <ul className="space-y-3">
                {[
                  'Set your own retail prices on every product',
                  'Keep 100% of the margin above wholesale cost',
                  'No minimum sales targets to maintain',
                  'Volume discounts unlock as you grow',
                  'Transparent monthly commission statements',
                ].map((item, i) => (
                  <li key={i} className="flex items-start gap-3 text-sm text-emerald-100">
                    <CheckCircle2 className="w-4 h-4 text-green-300 mt-0.5 flex-shrink-0" />
                    {item}
                  </li>
                ))}
              </ul>
            </div>
          </div>
        </div>

        {/* ── Your Business, Your Way ── */}
        <div className="bg-white/80 border border-gray-100 rounded-3xl shadow-sm p-8 sm:p-12 mb-16">
          <div className="text-center mb-10">
            <h2 className="text-2xl sm:text-3xl font-bold text-gray-900 mb-3">Your Business. Your Way.</h2>
            <p className="text-gray-500 max-w-2xl mx-auto">
              We handle the network, the infrastructure, and the compliance.
              You focus on customers, sales, and growing your brand.
            </p>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
            {[
              {
                icon: Building2,
                title: 'Sell Under Your Company',
                body: 'Operate as a fully independent telecoms provider. Customers sign up with you, pay you, and support through you — powered silently by Silux.',
              },
              {
                icon: Globe,
                title: 'Reach Any Market',
                body: 'Target any customer base — individuals, families, students, or SMEs. You know your market; we give you the products to serve them.',
              },
              {
                icon: Shield,
                title: 'Backed by a Trusted Network',
                body: 'All plans run on established UK networks. You get the credibility of a proven infrastructure without the cost of building your own.',
              },
              {
                icon: Zap,
                title: 'Ready to Go Fast',
                body: 'From approval to live in days, not months. Your branded portal is set up quickly so you can start selling and earning straight away.',
              },
              {
                icon: TrendingUp,
                title: 'Scale Without Limits',
                body: 'Whether you sell 5 SIMs a month or 500, the platform scales with you. More volume unlocks better rates and higher commission tiers.',
              },
              {
                icon: Users,
                title: 'Dedicated Reseller Support',
                body: "A dedicated account manager, onboarding assistance, and a reseller helpdesk — we're with you every step of the way.",
              },
            ].map(({ icon: Icon, title, body }, i) => (
              <div key={i} className="flex gap-4">
                <div className="flex-shrink-0 w-10 h-10 bg-primary-50 rounded-xl flex items-center justify-center">
                  <Icon className="w-5 h-5 text-primary-600" />
                </div>
                <div>
                  <h4 className="font-bold text-gray-900 mb-1">{title}</h4>
                  <p className="text-sm text-gray-500 leading-relaxed">{body}</p>
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* ── How It Works ── */}
        <div className="mb-16">
          <h2 className="text-3xl font-bold text-center text-gray-900 mb-10">How It Works</h2>
          <div className="grid grid-cols-1 sm:grid-cols-4 gap-0 relative">
            {[
              { step: '1', title: 'Apply', body: 'Complete the short application form below. Tell us about your business and what you plan to sell.' },
              { step: '2', title: 'Get Approved', body: 'Our team reviews your application within 2 business days and reaches out to discuss your partnership.' },
              { step: '3', title: 'Get Set Up', body: 'We configure your branded portal with your logo, colours and domain. You\'re walked through everything.' },
              { step: '4', title: 'Start Earning', body: 'Go live, sell to your customers at your prices, and receive transparent monthly commission payouts.' },
            ].map(({ step, title, body }, i, arr) => (
              <div key={i} className="flex sm:flex-col items-start sm:items-center gap-4 sm:gap-3 pb-6 sm:pb-0">
                <div className="flex items-center gap-3 sm:flex-col sm:gap-2">
                  <div className="flex-shrink-0 w-12 h-12 rounded-full bg-gradient-to-br from-primary-600 to-primary-500 text-white font-black text-xl flex items-center justify-center shadow-lg">
                    {step}
                  </div>
                  {i < arr.length - 1 && (
                    <ArrowRight className="w-5 h-5 text-gray-300 hidden sm:block" />
                  )}
                </div>
                <div className="sm:text-center sm:px-2">
                  <h4 className="font-bold text-gray-900 mb-1">{title}</h4>
                  <p className="text-sm text-gray-500 leading-relaxed">{body}</p>
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Benefits */}
        <div className="mb-16">
          <h2 className="text-3xl font-bold text-center text-gray-900 mb-12">Why Partner With Us?</h2>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
            {BENEFITS.map((benefit, i) => (
              <Card key={i} className="text-center p-6 backdrop-blur-sm bg-white/90 hover:shadow-xl transition-shadow">
                <div className="inline-flex p-4 bg-primary-100 rounded-xl mb-4">
                  <benefit.icon className="w-8 h-8 text-primary-600" />
                </div>
                <h3 className="font-bold text-lg text-gray-900 mb-2">{benefit.title}</h3>
                <p className="text-gray-600 text-sm">{benefit.description}</p>
              </Card>
            ))}
          </div>
        </div>

        {/* Commission Tiers */}
        <div className="mb-16">
          <h2 className="text-3xl font-bold text-center text-gray-900 mb-12">Commission Tiers</h2>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {TIERS.map((tier, i) => (
              <Card key={i} className="relative overflow-hidden backdrop-blur-sm bg-white/90 shadow-xl hover:shadow-2xl transition-all hover:scale-105">
                <div className={`h-2 bg-gradient-to-r ${tier.color}`}></div>
                <div className="p-8">
                  <h3 className="text-2xl font-bold text-gray-900 mb-2">{tier.name}</h3>
                  <div className="text-4xl font-bold bg-gradient-to-r ${tier.color} bg-clip-text text-transparent mb-2">
                    {tier.commission}
                  </div>
                  <p className="text-gray-600 mb-6">{tier.sales} sales/month</p>
                  <ul className="space-y-3">
                    {tier.features.map((feature, j) => (
                      <li key={j} className="flex items-center gap-2 text-gray-700">
                        <CheckCircle2 className="w-5 h-5 text-green-600 flex-shrink-0" />
                        <span>{feature}</span>
                      </li>
                    ))}
                  </ul>
                </div>
              </Card>
            ))}
          </div>
        </div>

        {/* Application Form */}
        <Card className="max-w-3xl mx-auto backdrop-blur-sm bg-white/90 shadow-xl p-4 sm:p-8">
          <div className="text-center mb-8">
            <div className="inline-flex p-3 bg-primary-100 rounded-xl mb-4">
              <Mail className="w-8 h-8 text-primary-600" />
            </div>
            <h2 className="text-3xl font-bold text-gray-900 mb-2">Apply Now</h2>
            <p className="text-gray-600">Fill in the form below and we'll get back to you soon</p>
          </div>

          <form onSubmit={handleSubmit} className="space-y-6">
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-2">First Name *</label>
                <input
                  type="text"
                  value={formData.first_name}
                  onChange={(e) => setFormData({ ...formData, first_name: e.target.value })}
                  className="w-full px-4 py-3 rounded-xl border-2 border-gray-200 focus:border-primary-500 focus:outline-none"
                  required
                />
              </div>
              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-2">Last Name *</label>
                <input
                  type="text"
                  value={formData.last_name}
                  onChange={(e) => setFormData({ ...formData, last_name: e.target.value })}
                  className="w-full px-4 py-3 rounded-xl border-2 border-gray-200 focus:border-primary-500 focus:outline-none"
                  required
                />
              </div>
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-2">Email *</label>
                <input
                  type="email"
                  value={formData.email}
                  onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                  className="w-full px-4 py-3 rounded-xl border-2 border-gray-200 focus:border-primary-500 focus:outline-none"
                  required
                />
              </div>
              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-2">Phone *</label>
                <input
                  type="tel"
                  value={formData.phone}
                  onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
                  className="w-full px-4 py-3 rounded-xl border-2 border-gray-200 focus:border-primary-500 focus:outline-none"
                  required
                />
              </div>
            </div>

            <div>
              <label className="block text-sm font-semibold text-gray-700 mb-2">Company Name</label>
              <input
                type="text"
                value={formData.company_name}
                onChange={(e) => setFormData({ ...formData, company_name: e.target.value })}
                className="w-full px-4 py-3 rounded-xl border-2 border-gray-200 focus:border-primary-500 focus:outline-none"
                placeholder="Your company (optional)"
              />
            </div>

            <div>
              <label className="block text-sm font-semibold text-gray-700 mb-2">Website</label>
              <input
                type="text"
                inputMode="url"
                value={formData.website}
                onChange={(e) => setFormData({ ...formData, website: e.target.value })}
                className="w-full px-4 py-3 rounded-xl border-2 border-gray-200 focus:border-primary-500 focus:outline-none"
                placeholder="e.g. silux.co.uk or www.silux.co.uk"
              />
            </div>

            <div>
              <label className="block text-sm font-semibold text-gray-700 mb-2">Expected Monthly Sales *</label>
              <select
                value={formData.expected_monthly_sales}
                onChange={(e) => setFormData({ ...formData, expected_monthly_sales: parseInt(e.target.value) || 0 })}
                className="w-full px-4 py-4 text-base rounded-xl border-2 border-gray-200 focus:border-primary-500 focus:outline-none appearance-none bg-white cursor-pointer"
                size={1}
                required
              >
                <option value="0">— Select range —</option>
                <option value="10">5–25 sales / month</option>
                <option value="35">26–50 sales / month</option>
                <option value="75">51–100 sales / month</option>
                <option value="150">101+ sales / month</option>
              </select>
            </div>

            <div>
              <label className="block text-sm font-semibold text-gray-700 mb-2">Why do you want to become a reseller? *</label>
              <textarea
                value={formData.experience}
                onChange={(e) => setFormData({ ...formData, experience: e.target.value })}
                rows={4}
                className="w-full px-4 py-3 rounded-xl border-2 border-gray-200 focus:border-primary-500 focus:outline-none"
                placeholder="Tell us about your experience and why you want to partner with us..."
                required
              />
            </div>

            <div className="flex justify-center">
              <ReCAPTCHA
                ref={recaptchaRef}
                sitekey={import.meta.env.VITE_RECAPTCHA_SITE_KEY}
                onChange={setRecaptchaToken}
                onExpired={() => setRecaptchaToken(null)}
              />
            </div>

            {errorMessage && (
              <div className="flex items-start gap-3 p-4 bg-red-50 border border-red-200 rounded-xl text-red-700 text-sm">
                <span className="font-semibold">Error:</span>
                <span>{errorMessage}</span>
              </div>
            )}

            <button
              type="submit"
              disabled={applyMutation.isPending || !recaptchaToken}
              className={`w-full py-4 px-6 rounded-xl font-bold text-lg transition-all ${
                !applyMutation.isPending && recaptchaToken
                  ? 'bg-gradient-to-r from-primary-600 to-primary-500 hover:from-primary-700 hover:to-primary-600 text-white shadow-xl hover:shadow-2xl transform hover:scale-[1.02]'
                  : 'bg-gray-200 text-gray-400 cursor-not-allowed'
              }`}
            >
              {applyMutation.isPending ? (
                <span className="flex items-center justify-center gap-2">
                  <Loader2 className="w-6 h-6 animate-spin" />
                  Submitting...
                </span>
              ) : (
                'Submit Application'
              )}
            </button>
          </form>
        </Card>
      </div>
    </div>
  )
}
